import React from 'react';
import { Loader2Icon, LogOutIcon } from 'lucide-react';
import { SiteHeader } from '../components/ui/SiteHeader';
import { SiteFooter } from '../components/ui/SiteFooter';
import { MembershipSummary } from '../components/membership/MembershipSummary';
import { MembershipSignIn } from '../components/membership/MembershipSignIn';
import { useMembershipManagement, type MembershipPreview } from '../hooks/useMembershipManagement';
import { supportEmail } from '../data/cancellationReasons';
import { useScreenInit } from '../useScreenInit.js';

/**
 * /practitioners/account/membership — practitioner Manage Membership page.
 *
 * Membership information comes from the backend.
 * Billing changes and cancellation are handled through Stripe Customer Portal.
 */
export function PractitionerAccountMembershipPage() {
  // Design-canvas preview only (Screens iframe); ignored on normal visits.
  const screen = useScreenInit() as MembershipPreview;
  const preview = window.self !== window.top && screen.membershipPreview ? screen : undefined;
  const m = useMembershipManagement(preview);
  const {
    load
  } = m;

  // Sign out is offered whenever a practitioner session is active
  // (or in design-canvas previews of signed-in states).
  const showSignOut = load.kind !== 'signed_out' && load.kind !== 'loading' && (Boolean(m.session) || Boolean(preview));
  return <div className="min-h-screen bg-ivory font-sans text-charcoal selection:bg-purple-200 selection:text-charcoal flex flex-col" data-id="element-852" data-mp="c5fwt5wohouh">
      <SiteHeader currentPath="/practitioners" data-id="element-853" data-mp="h52d5o7szl7i" />

      <main className="flex-1 max-w-5xl w-full mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-24 md:pb-32" data-id="element-854" data-mp="l5w1gip5r20x">
        <header className="mb-10 md:mb-12 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between" data-id="element-855" data-mp="pwktbz4r210l">
          <div data-id="element-856" data-mp="3pkc9efp0hw7d">
            <p className="font-serif italic text-lg text-purple-700" data-id="element-857" data-mp="3pkvuu6dvf2q5">
              Practitioner account
            </p>

            <h1 className="mt-2 font-serif text-4xl md:text-6xl text-charcoal leading-[1.05] tracking-tight" data-id="element-858" data-mp="3tkczixlh3vu9">
              Your membership
            </h1>
          </div>

          {showSignOut && <div className="flex flex-col items-start gap-2 sm:items-end" data-id="element-859" data-mp="vrb77oifil5y">
              {m.session?.email && <p className="text-sm text-charcoal/60" data-id="element-860" data-mp="1ag7evkgkpw61">
                  Signed in as{' '}
                  <span className="font-medium text-charcoal" data-id="element-861" data-mp="15ipgup43h1ct">
                    {m.session.email}
                  </span>
                </p>}

              <button type="button" onClick={() => void m.signOut()} disabled={m.signingOut} className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-charcoal/20 px-5 py-2.5 text-sm font-semibold text-charcoal transition-colors duration-200 ease-out hover:border-purple-700 hover:text-purple-800 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2 focus-visible:ring-offset-ivory" data-id="element-862" data-mp="cu1qwg8ehnsx">
                {m.signingOut ? <Loader2Icon aria-hidden="true" className="h-4 w-4 animate-spin" data-id="element-863" data-mp="2aqs1lpnz13ve" /> : <LogOutIcon aria-hidden="true" className="h-4 w-4" data-id="element-864" data-mp="1vtr75eeeaut8" />}
                {m.signingOut ? 'Signing out…' : 'Sign out'}
              </button>
            </div>}
        </header>

        {load.kind === 'signed_out' && m.signedOut && <p role="status" className="mb-6 max-w-lg rounded-xl bg-purple-50 px-4 py-3 text-sm text-purple-900" data-id="element-865" data-mp="2ten8qqp2vjfj">
            You’ve been signed out.
          </p>}

        {load.kind === 'loading' && <div aria-live="polite" aria-busy="true" className="flex items-center gap-3 py-16 text-charcoal/70" data-id="element-866" data-mp="2jniuxub524ma">
            <Loader2Icon aria-hidden="true" className="h-5 w-5 animate-spin text-purple-700" data-id="element-867" data-mp="3c6y501p88j4o" />
            Loading your membership…
          </div>}

        {load.kind === 'signed_out' && <MembershipSignIn submitting={m.signingIn} error={m.signInError} onSubmit={m.signIn} data-id="element-868" data-mp="1jl4i60uivtdk" />}

        {load.kind === 'none' && <section className="max-w-xl" data-id="element-869" data-mp="19mou66qsl08a">
            <h2 className="font-serif text-2xl text-charcoal" data-id="element-870" data-mp="278vydfj6oqk4">
              You don’t have a membership yet
            </h2>

            <p className="mt-3 text-base text-charcoal/70 leading-relaxed" data-id="element-871" data-mp="22akkmtprpvwm">
              Choose a practitioner membership to join Fem Entity.
            </p>

            <a href="/practitioners/membership" className="mt-7 inline-flex rounded-full bg-purple-700 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 ease-out hover:bg-purple-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2 focus-visible:ring-offset-ivory" data-id="element-872" data-mp="1sjq1vzbqdefx">
              Choose a membership
            </a>
          </section>}

        {load.kind === 'unavailable' && <section role="alert" className="max-w-xl rounded-3xl border border-charcoal/10 bg-white px-6 py-8 md:px-9" data-id="element-873" data-mp="32h6ij2nzwchm">
            <h2 className="font-serif text-2xl text-charcoal" data-id="element-874" data-mp="37eg4u9g38818">
              {load.notConnected ? 'Membership management is coming soon' : 'We couldn’t load your membership'}
            </h2>

            <p className="mt-3 text-base text-charcoal/70 leading-relaxed" data-id="element-875" data-mp="3l87fxm9gvzz2">
              {load.notConnected ? 'You’ll be able to view and manage your membership here shortly. For help in the meantime, contact us.' : 'Your membership hasn’t been changed. Please try again in a moment.'}
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-4" data-id="element-876" data-mp="40lsh6pblxi6">
              {!load.notConnected && <button type="button" onClick={() => void m.reload()} className="inline-flex rounded-full bg-purple-700 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 ease-out hover:bg-purple-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2" data-id="element-877" data-mp="3ahklnh4ssenz">
                  Try Again
                </button>}

              <a href={`mailto:${supportEmail}`} className="text-sm font-medium text-purple-700 underline underline-offset-4 decoration-purple-300 hover:text-purple-900 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600" data-id="element-878" data-mp="3o98wlh3b8x3d">
                Contact Support
              </a>
            </div>
          </section>}

        {load.kind === 'ready' && <>
            <MembershipSummary membership={load.membership} actionBusy={m.actionBusy} actionError={m.actionError} onManage={() => void m.manage()} data-id="element-879" data-mp="23uyvd6rmb57l" />

            <p className="mt-6 text-sm text-charcoal/55" data-id="element-880" data-mp="9ks55dm26epj">
              Billing and membership cancellation are handled securely by
              Stripe. Questions?{' '}
              <a href={`mailto:${supportEmail}`} className="font-medium text-purple-700 underline underline-offset-2 decoration-purple-300 hover:text-purple-900 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600" data-id="element-881" data-mp="ehs7pejp8zbw">
                {supportEmail}
              </a>
            </p>
          </>}
      </main>

      <SiteFooter currentPath="/practitioners" data-id="element-882" data-mp="3rqn6mbdg4sha" />
    </div>;
}